import { Button, Skeleton } from '@monksflow/monks-ui';
import { RefreshCwIcon, SparklesIcon, VideoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VideoSectionProps } from '../tabs/ProfileMediaTab/components/VideoSection/VideoSection.types';
import { VideoCardV2 } from './VideoCardV2';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm font-medium text-foreground">{children}</span>
);

const VideoSkeleton = ({ label, statusLabel }: { label: string; statusLabel?: string }) => (
  <div className="flex flex-col gap-xs">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex aspect-video flex-col items-center justify-center gap-xs rounded-md border border-dashed border-border bg-muted">
      <Skeleton className="size-10 rounded-full" />
      <span className="text-xs text-muted-foreground">{statusLabel}</span>
    </div>
  </div>
);

export function VideoSectionV2({
  thumbnailVideoAssetId,
  detailVideoAssetId,
  isProcessing,
  processingLabel,
  onRegenerateClick,
  onGenerateClick,
  thumbnailMetadata,
  detailMetadata,
  thumbnailFailed,
  detailFailed,
  thumbnailErrorMessage,
  detailErrorMessage,
  retryingVideoType,
  onRetryVideo,
}: VideoSectionProps) {
  const { t } = useTranslation('audiences');

  const hasVideos = Boolean(
    thumbnailVideoAssetId || detailVideoAssetId || thumbnailFailed || detailFailed,
  );
  const statusLabel = isProcessing
    ? (processingLabel ?? t('audiences.videoSection.generating'))
    : undefined;
  // A single-video retry renders inline on the affected card rather than
  // replacing the whole section with skeletons.
  const isRetrying = retryingVideoType != null;

  if (isProcessing && !isRetrying) {
    return (
      <div className="flex flex-col gap-sm">
        <SectionHeading>{t('audiences.videoSection.thumbnailDetailVideo')}</SectionHeading>
        <div className="grid grid-cols-1 gap-md md:grid-cols-2">
          <VideoSkeleton
            label={t('audiences.videoSection.thumbnailVideo')}
            statusLabel={statusLabel}
          />
          <VideoSkeleton
            label={t('audiences.videoSection.detailVideo')}
            statusLabel={statusLabel}
          />
        </div>
      </div>
    );
  }

  if (!hasVideos) {
    return (
      <div className="flex flex-col gap-sm">
        <SectionHeading>{t('audiences.videoSection.thumbnailDetailVideo')}</SectionHeading>
        <div className="flex flex-col items-center justify-center gap-md rounded-md border border-dashed border-border bg-muted py-10">
          <div className="flex size-10 items-center justify-center rounded-full bg-muted-foreground/10">
            <VideoIcon className="size-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col items-center gap-3xs">
            <span className="text-sm text-muted-foreground">
              {t('audiences.videoSection.noVideosGeneratedYet')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('audiences.videoSection.generateThumbnailAndDetailVideos')}
            </span>
          </div>
          <Button onClick={() => void onGenerateClick()} disabled={isProcessing}>
            <SparklesIcon className="size-4" />
            {t('audiences.videoSection.generateVideos')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center justify-between gap-sm">
        <div className="flex items-center gap-xs">
          <SectionHeading>{t('audiences.videoSection.thumbnailDetailVideo')}</SectionHeading>
          {statusLabel && <span className="text-xs text-muted-foreground">{statusLabel}</span>}
        </div>
        <Button variant="outline" size="sm" onClick={onRegenerateClick} disabled={isProcessing}>
          <RefreshCwIcon className="size-4" />
          {t('audiences.videoSection.reGenerateVideo')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-md md:grid-cols-2">
        <VideoCardV2
          label={t('audiences.videoSection.thumbnailVideo')}
          videoAssetId={thumbnailVideoAssetId}
          metadata={thumbnailMetadata}
          hasError={thumbnailFailed}
          errorMessage={thumbnailErrorMessage}
          isRetrying={retryingVideoType === 'mouseOverVideo'}
          onRetry={onRetryVideo ? () => onRetryVideo('mouseOverVideo') : undefined}
        />
        <VideoCardV2
          label={t('audiences.videoSection.detailVideo')}
          videoAssetId={detailVideoAssetId}
          metadata={detailMetadata}
          hasError={detailFailed}
          errorMessage={detailErrorMessage}
          isRetrying={retryingVideoType === 'video'}
          onRetry={onRetryVideo ? () => onRetryVideo('video') : undefined}
        />
      </div>
    </div>
  );
}

VideoSectionV2.displayName = 'VideoSectionV2';
